import { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import {
  Plus,
  ChevronDown,
  ChevronRight,
  Check,
  X,
  MessageSquare,
  GripVertical,
  Copy,
  Trash2,
  SlidersHorizontal,
  EyeOff,
  Eye,
  PanelBottomClose,
  PanelBottomOpen,
  Pin,
  Archive,
  ArchiveRestore,
} from "lucide-react";
import {
  useAllSubTasks,
  useCreateSubTask,
  useUpdateSubTask,
  useReorderSubTasks,
  useReparentSubTask,
  useDeleteSubTask,
} from "@/features/tasks/hooks/useSubTasks";
import { SubTaskBoardCard } from "@/features/tasks/components/SubTaskBoardCard";
import { SubTaskDetailPanel } from "@/features/tasks/components/SubTaskDetailPanel";
import { TaskDetailPanel } from "@/features/tasks/components/TaskDetailPanel";
import { CreateTaskModal } from "@/features/tasks/components/CreateTaskModal";
import {
  useTasks,
  useUpdateTask,
  useDeleteTask,
  useAssignUser,
  useUnassignUser,
  useCloneTasks,
  useReorderTasks,
  usePinTask,
  useArchiveTask,
  useTaskUIPrefs,
  useSetTaskUIPrefs,
} from "@/features/tasks/hooks/useTasks";
import { UserMultiSelect } from "@/features/tasks/components/UserMultiSelect";
import { WeekSelect } from "@/components/ui/WeekSelect";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { UserAvatar } from "@/components/ui/UserAvatar";
import {
  TASK_STATUS_LABELS,
  TASK_STATUS_COLORS,
  TASK_STATUS_BORDER,
  TASK_STATUS_ORDER,
  PRIORITY_LABELS,
  PRIORITY_COLORS,
  PRIORITY_DOT,
  type TaskStatus,
  type TaskPriority,
  type SubTaskFilters,
  type TaskFilters,
  type SubTask,
  type Task,
  type InternalUser,
} from "@/features/tasks/types";
import { useProjects } from "@/features/tasks/hooks/useProjects";
import { useInternalUsers } from "@/features/tasks/hooks/useInternalUsers";
import { useClientsForTasks } from "@/features/tasks/hooks/useClients";
import { useTaskCategories } from "@/features/tasks/hooks/useTaskCategories";
import { CategoryFilter } from "@/features/tasks/components/CategoryFilter";
import { WorkProgressSection } from "@/features/tasks/components/WorkProgressSection";
import { useAuthStore } from "@/store/auth";
import { PageLoader } from "@/components/ui/Spinner";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

// ── Session persistence ────────────────────────────────────────────────────────

const FRIDAY_BOARD_SESSION_KEY = "friday.board.filters";

type PersistedBoardFilters = SubTaskFilters & { _searchInput?: string; _userSearch?: string; _showArchived?: boolean };

function readFridayBoardSession(): PersistedBoardFilters | null {
  try {
    const raw = sessionStorage.getItem(FRIDAY_BOARD_SESSION_KEY);
    return raw ? (JSON.parse(raw) as PersistedBoardFilters) : null;
  } catch {
    return null;
  }
}

// ── CloneTasksModal ────────────────────────────────────────────────────────

interface CloneTasksModalProps {
  tasks: Task[];
  allTaskIds: string[];
  onClose: () => void;
}

function CloneTasksModal({ tasks, allTaskIds, onClose }: CloneTasksModalProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    new Set(allTaskIds),
  );
  const [targetProjectId, setTargetProjectId] = useState("");
  const [success, setSuccess] = useState(false);
  const { data: projects = [] } = useProjects();
  const { mutate: cloneTasks, isPending } = useCloneTasks();

  const sourceProjectIds = new Set(
    tasks
      .filter((t) => selectedIds.has(t.id) && t.project)
      .map((t) => t.project as string),
  );
  const availableProjects = projects.filter((p) => !sourceProjectIds.has(p.id));

  useEffect(() => {
    if (targetProjectId && sourceProjectIds.has(targetProjectId)) {
      setTargetProjectId("");
    }
  }, [targetProjectId, selectedIds]);

  function toggleTask(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll() {
    if (selectedIds.size === tasks.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(tasks.map((t) => t.id)));
  }

  function handleClone() {
    if (!targetProjectId || selectedIds.size === 0) return;
    cloneTasks(
      { task_ids: Array.from(selectedIds), target_project_id: targetProjectId },
      {
        onSuccess: () => setSuccess(true),
      },
    );
  }

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg flex flex-col max-h-[80vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Klonuj zadania do przedsięwzięcia
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={18} />
          </button>
        </div>

        {success ? (
          <div className="flex flex-col items-center justify-center gap-3 py-12 px-6">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
              <Check size={24} className="text-green-600" />
            </div>
            <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
              Sklonowano {selectedIds.size}{" "}
              {selectedIds.size === 1
                ? "zadanie"
                : selectedIds.size < 5
                  ? "zadania"
                  : "zadań"}{" "}
              z podzadaniami.
            </p>
            <button
              onClick={onClose}
              className="mt-2 px-4 py-2 bg-[var(--color-primary)] text-white text-sm rounded-lg hover:bg-[var(--color-primary-hover)]"
            >
              Zamknij
            </button>
          </div>
        ) : (
          <>
            {/* Project picker */}
            <div className="px-6 pt-4 pb-2">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1.5">
                Docelowe przedsięwzięcie
              </label>
              <SearchableSelect
                placeholder="— wybierz przedsięwzięcie —"
                value={targetProjectId}
                onChange={(e) => setTargetProjectId(e.target.value)}
                options={availableProjects.map((p) => ({ value: p.id, label: `${p.short_name} — ${p.name}` }))}
              />
            </div>

            {/* Task list */}
            <div className="px-6 py-2 flex-1 overflow-y-auto">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                  Wybierz zadania ({selectedIds.size}/{tasks.length})
                </span>
                <button
                  onClick={toggleAll}
                  className="text-xs text-[var(--color-primary)] hover:underline"
                >
                  {selectedIds.size === tasks.length
                    ? "Odznacz wszystkie"
                    : "Zaznacz wszystkie"}
                </button>
              </div>
              <div className="flex flex-col gap-1">
                {tasks.map((task) => (
                  <label
                    key={task.id}
                    className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedIds.has(task.id)}
                      onChange={() => toggleTask(task.id)}
                      className="w-4 h-4 rounded border-gray-300 text-[var(--color-primary)] focus:ring-[var(--color-primary)]"
                    />
                    <span className="text-sm text-gray-800 dark:text-gray-200 truncate">
                      {task.title}
                    </span>
                    {task.project_name && (
                      <span className="text-xs text-[var(--color-primary)] bg-blue-50 dark:bg-blue-900/30 px-1.5 py-0.5 rounded flex-shrink-0">
                        {task.project_name}
                      </span>
                    )}
                  </label>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between gap-3">
              <p className="text-xs text-gray-400 dark:text-gray-500">
                Kopiuje tytuły i podzadania. Bez przypisanych osób i terminów.
              </p>
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 "
                >
                  Anuluj
                </button>
                <button
                  onClick={handleClone}
                  disabled={
                    isPending || !targetProjectId || selectedIds.size === 0
                  }
                  className="px-4 py-2 text-sm bg-[var(--color-primary)] text-white rounded-lg hover:bg-[var(--color-primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isPending ? "Klonowanie..." : `Klonuj (${selectedIds.size})`}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body,
  );
}

// ── CreateSubTaskModal ─────────────────────────────────────────────────────

const subTaskSchema = z.object({
  taskId: z.string().min(1),
  title: z.string().min(1, "Tytuł jest wymagany").max(255),
  priority: z.enum(["low", "medium", "high"] as const),
  status: z.enum([
    "todo_later",
    "todo_now",
    "in_progress",
    "waiting",
    "done",
    "cancelled",
    "n_d",
  ] as const),
});

type SubTaskFormValues = z.infer<typeof subTaskSchema>;

interface CreateSubTaskModalProps {
  open: boolean;
  onClose: () => void;
  defaultStatus?: TaskStatus;
  tasks: { id: string; title: string; project_name: string | null }[];
  users: { id: string; full_name: string }[];
}

function CreateSubTaskModal({
  open,
  onClose,
  defaultStatus = "todo_now",
  tasks,
  users,
}: CreateSubTaskModalProps) {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<SubTaskFormValues>({
    resolver: zodResolver(subTaskSchema),
    defaultValues: { priority: "medium", status: defaultStatus, taskId: "" },
  });
  const [dueDate, setDueDate] = useState<string | null>(null);

  const selectedTaskId = watch("taskId");
  const { mutate: createSubTask, isPending } = useCreateSubTask(selectedTaskId);
  const [selectedAssignees, setSelectedAssignees] = useState<string[]>([]);

  function toggleAssignee(id: string) {
    setSelectedAssignees((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function onSubmit(values: SubTaskFormValues) {
    const { taskId, ...rest } = values;
    createSubTask(
      { ...rest, due_date: dueDate, assignee_ids: selectedAssignees },
      {
        onSuccess: () => {
          reset();
          setDueDate(null);
          setSelectedAssignees([]);
          onClose();
        },
      },
    );
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg mx-4 flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Nowe podzadanie
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600"
          >
            <X size={20} />
          </button>
        </div>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4 px-6 py-4 overflow-y-auto"
        >
          {/* Parent task */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Zadanie nadrzędne <span className="text-red-500">*</span>
            </label>
            <SearchableSelect
              placeholder="— wybierz zadanie —"
              options={tasks.map((t) => ({
                value: t.id,
                label: (t.project_name ? `[${t.project_name}] ` : "") + t.title,
              }))}
              error={errors.taskId ? "Wybierz zadanie nadrzędne" : undefined}
              {...register("taskId")}
            />
          </div>

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Tytuł <span className="text-red-500">*</span>
            </label>
            <input
              {...register("title")}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
              placeholder="Np. Sprawdzić dokumentację..."
            />
            {errors.title && (
              <p className="text-xs text-red-500 mt-1">
                {errors.title.message}
              </p>
            )}
          </div>

          {/* Status + Priority */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Status
              </label>
              <select
                {...register("status")}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
              >
                {(TASK_STATUS_ORDER as TaskStatus[]).map((s) => (
                  <option key={s} value={s}>
                    {TASK_STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Priorytet
              </label>
              <select
                {...register("priority")}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
              >
                {(Object.keys(PRIORITY_LABELS) as TaskPriority[]).map((p) => (
                  <option key={p} value={p}>
                    {PRIORITY_LABELS[p]}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Due week */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Tydzień realizacji
            </label>
            <WeekSelect
              value={dueDate}
              onChange={setDueDate}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
            />
          </div>

          {/* Assignees */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Przypisani
            </label>
            <div className="flex flex-wrap gap-2 max-h-28 overflow-y-auto">
              {users.map((u) => {
                const sel = selectedAssignees.includes(u.id);
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => toggleAssignee(u.id)}
                    className={`flex items-center gap-1.5 text-xs px-2 py-1 border transition-colors ${
                      sel
                        ? "bg-[var(--color-primary)] text-white border-[var(--color-primary)]"
                        : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-300 hover:border-[var(--color-primary)]"
                    }`}
                  >
                    <UserAvatar
                      userId={u.id}
                      fullName={u.full_name}
                      profilePictureUrl={u.profile_picture_url}
                      size="xs"
                      initialsClassName={
                        sel ? "bg-white/30 text-white font-bold" : undefined
                      }
                    />
                    {u.full_name}
                  </button>
                );
              })}
            </div>
          </div>
        </form>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900"
          >
            Anuluj
          </button>
          <button
            onClick={handleSubmit(onSubmit)}
            disabled={isPending || !selectedTaskId}
            className="px-4 py-2 text-sm bg-[var(--color-primary)] text-white rounded-lg hover:bg-[var(--color-primary-hover)] disabled:opacity-50"
          >
            {isPending ? "Tworzenie..." : "Utwórz podzadanie"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── SubTaskTitleCell ───────────────────────────────────────────────────────

function SubTaskTitleCell({
  subtask,
  onSingleClick,
}: {
  subtask: SubTask;
  onSingleClick: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(subtask.title);
  const { mutate: updateSubTask } = useUpdateSubTask(subtask.task);
  const inputRef = useRef<HTMLInputElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setValue(subtask.title);
  }, [subtask.title]);
  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);
  // cleanup timer on unmount
  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  function handleClick(e: React.MouseEvent) {
    e.stopPropagation();
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(onSingleClick, 220);
  }

  function handleDoubleClick(e: React.MouseEvent) {
    e.stopPropagation();
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setEditing(true);
  }

  function save() {
    const trimmed = value.trim();
    if (trimmed && trimmed !== subtask.title)
      updateSubTask({ id: subtask.id, data: { title: trimmed } });
    else setValue(subtask.title);
    setEditing(false);
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            save();
          }
          if (e.key === "Escape") {
            setValue(subtask.title);
            setEditing(false);
          }
        }}
        onClick={(e) => e.stopPropagation()}
        className={`text-sm w-full px-2 py-0.5 border border-[var(--color-primary)] rounded focus:outline-none bg-white dark:bg-gray-700 ${["done", "cancelled", "n_d"].includes(subtask.status) ? "line-through text-gray-400" : "text-gray-800 dark:text-gray-200"}`}
      />
    );
  }

  return (
    <span
      className={`text-sm truncate cursor-pointer ${["done", "cancelled", "n_d"].includes(subtask.status) ? "line-through text-gray-400 dark:text-gray-500" : "text-gray-800 dark:text-gray-200 hover:text-[var(--color-primary)]"}`}
      title="Klik: otwórz szczegóły; 2× klik: edytuj tytuł"
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
    >
      {subtask.title}
    </span>
  );
}

// ── SubTaskPeopleCell ──────────────────────────────────────────────────────

function SubTaskPeopleCell({
  subtask,
  users,
}: {
  subtask: SubTask;
  users: InternalUser[];
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [dropPos, setDropPos] = useState({ top: 0, right: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const { mutate: updateSubTask } = useUpdateSubTask(subtask.task);

  useEffect(() => {
    if (!open) return;
    function onOutside(e: MouseEvent) {
      if (
        dropRef.current &&
        !dropRef.current.contains(e.target as Node) &&
        btnRef.current &&
        !btnRef.current.contains(e.target as Node)
      )
        setOpen(false);
    }
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [open]);

  const DROPDOWN_H = 280;

  function openDropdown(e: React.MouseEvent) {
    e.stopPropagation();
    if (open) {
      setOpen(false);
      return;
    }
    const rect = btnRef.current!.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const top =
      spaceBelow >= DROPDOWN_H
        ? rect.bottom + 4
        : Math.max(8, rect.top - DROPDOWN_H - 4);
    setDropPos({ top, right: window.innerWidth - rect.right });
    setSearch("");
    setOpen(true);
  }

  const assignedIds = subtask.assignees.map((a) => a.id);
  const filtered = users
    .filter((u) => u.full_name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => a.full_name.localeCompare(b.full_name, "pl"));

  function toggle(userId: string) {
    const newIds = assignedIds.includes(userId)
      ? assignedIds.filter((id) => id !== userId)
      : [...assignedIds, userId];
    updateSubTask({ id: subtask.id, data: { assignee_ids: newIds } });
  }

  return (
    <div
      className="flex items-center gap-1"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex -space-x-1.5">
        {subtask.assignees.slice(0, 3).map((a) => (
          <UserAvatar
            key={a.id}
            userId={a.id}
            fullName={a.full_name}
            profilePictureUrl={a.profile_picture_url}
            size="sm"
            border
          />
        ))}
        {subtask.assignees.length > 3 && (
          <div className="w-6 h-6 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-xs flex items-center justify-center border-2 border-white dark:border-gray-800">
            +{subtask.assignees.length - 3}
          </div>
        )}
      </div>
      <button
        ref={btnRef}
        onClick={openDropdown}
        className="w-5 h-5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-400 hover:bg-blue-100 dark:hover:bg-blue-900/40 hover:text-[var(--color-primary)] flex items-center justify-center transition-colors flex-shrink-0"
        title="Zarządzaj osobami"
      >
        <Plus size={10} />
      </button>
      {open &&
        createPortal(
          <div
            ref={dropRef}
            style={{
              top: dropPos.top,
              right: dropPos.right,
              position: "fixed",
            }}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-[200] w-52"
          >
            <div className="p-2 border-b border-gray-100 dark:border-gray-700">
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
                placeholder="Szukaj osoby..."
                className="w-full text-xs px-2 py-1.5 rounded focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)] bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 placeholder:text-gray-400"
              />
            </div>
            <div className="max-h-48 overflow-y-auto py-1">
              {filtered.map((u) => {
                const assigned = assignedIds.includes(u.id);
                return (
                  <button
                    key={u.id}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      toggle(u.id);
                    }}
                    className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-[var(--color-primary-dim)] dark:hover:bg-blue-900/30 transition-colors"
                  >
                    <UserAvatar
                      fullName={u.full_name}
                      profilePictureUrl={u.profile_picture_url}
                      size="xs"
                    />
                    <span
                      className={`flex-1 truncate ${assigned ? "text-gray-900 dark:text-gray-100 font-medium" : "text-gray-600 dark:text-gray-400"}`}
                    >
                      {u.full_name}
                    </span>
                    {assigned && (
                      <Check
                        size={12}
                        className="text-[var(--color-primary)] flex-shrink-0"
                      />
                    )}
                  </button>
                );
              })}
              {filtered.length === 0 && (
                <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-3">
                  Brak wyników
                </p>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

// ── TableSubTaskRow ────────────────────────────────────────────────────────

function formatTimeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86400000);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  if (months >= 1) return `${months} mies. temu`;
  if (weeks >= 1) return `${weeks} tyg. temu`;
  if (days >= 1) return `${days} ${days === 1 ? "dzień" : "dni"} temu`;
  const hours = Math.floor(diff / 3600000);
  if (hours >= 1) return `${hours} godz. temu`;
  return "przed chwilą";
}

function weekText(weeks: number | null): { text: string; cls: string } | null {
  if (weeks === null) return null;
  const text =
    weeks === 0 ? "0 tyg." : weeks > 0 ? `+${weeks} tyg.` : `${weeks} tyg.`;
  const cls =
    weeks < -1
      ? "text-red-700 font-semibold"
      : weeks === -1
        ? "text-red-500 font-medium"
        : weeks === 0
          ? "text-orange-600 font-semibold"
          : weeks === 1
            ? "text-yellow-600"
            : "text-gray-400 dark:text-gray-500";
  return { text, cls };
}

function StatusSelect({ subtask }: { subtask: SubTask }) {
  const { mutate: updateSubTask } = useUpdateSubTask(subtask.task);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div
      ref={ref}
      className="relative w-full"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        onClick={() => setOpen((o) => !o)}
        className={`text-xs font-semibold px-2 py-1 rounded-none cursor-pointer flex items-center justify-center gap-1 w-full ${TASK_STATUS_COLORS[subtask.status]}`}
      >
        {TASK_STATUS_LABELS[subtask.status]}
        <ChevronDown size={9} className="opacity-60" />
      </button>
      {open &&
        createPortal(
          <div
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              position: "fixed",
              top: (ref.current?.getBoundingClientRect().bottom ?? 0) + 4,
              left: ref.current?.getBoundingClientRect().left ?? 0,
              zIndex: 9999,
            }}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg py-1 min-w-max"
          >
            {TASK_STATUS_ORDER.map((s) => (
              <button
                key={s}
                onClick={() => {
                  updateSubTask({ id: subtask.id, data: { status: s } });
                  setOpen(false);
                }}
                className={`w-full text-left px-3 py-1.5 text-xs font-semibold ${TASK_STATUS_COLORS[s]} ${s === subtask.status ? "ring-1 ring-inset ring-current" : "hover:brightness-95"}`}
              >
                {TASK_STATUS_LABELS[s]}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}

function TableSubTaskRow({
  subtask,
  onClick,
  users,
  titleIndent = 0,
  onAddChild,
  hasChildren,
  isCollapsed,
  onToggleCollapse,
  isChecked,
  isParentChecked,
  onToggleSelect,
}: {
  subtask: SubTask;
  onClick: () => void;
  users: InternalUser[];
  titleIndent?: number;
  onAddChild?: () => void;
  hasChildren?: boolean;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  isChecked?: boolean;
  isParentChecked?: boolean;
  onToggleSelect?: () => void;
}) {
  const { mutate: updateSubTask } = useUpdateSubTask(subtask.task);
  const { mutate: deleteSubTask } = useDeleteSubTask(subtask.task);
  const wk = weekText(subtask.weeks_until_due);

  function handleDelete(e: React.MouseEvent) {
    e.stopPropagation();
    if (!window.confirm("Usunąć to podzadanie?")) return;
    deleteSubTask(subtask.id);
  }

  return (
    <div className="grid grid-cols-[1fr_120px_110px_110px_80px_120px] items-center hover:bg-[var(--color-primary-dim)]/60 dark:hover:bg-blue-900/10 transition-colors group/strow">
      {/* Title — single click opens panel, double-click edits */}
      <div
        className="py-1 truncate border-r border-gray-200 dark:border-gray-600 flex items-center gap-1"
        style={{ paddingLeft: `${12 + titleIndent}px` }}
      >
        {onToggleSelect !== undefined && (
          <input
            type="checkbox"
            checked={isChecked || isParentChecked}
            disabled={isParentChecked}
            onChange={() => onToggleSelect()}
            onClick={(e) => e.stopPropagation()}
            className={`flex-shrink-0 w-3.5 h-3.5 rounded cursor-pointer accent-blue-500 transition-opacity ${(isChecked || isParentChecked) ? "opacity-100" : "opacity-0 group-hover/strow:opacity-100"}`}
          />
        )}
        {hasChildren ? (
          <button
            onClick={(e) => { e.stopPropagation(); onToggleCollapse?.(); }}
            className="flex-shrink-0 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
            title={isCollapsed ? "Rozwiń podzadania" : "Zwiń podzadania"}
          >
            {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
          </button>
        ) : titleIndent > 0 ? (
          <span className="text-gray-300 dark:text-gray-600 text-xs font-mono flex-shrink-0 select-none leading-none">
            └─
          </span>
        ) : null}
        <SubTaskTitleCell subtask={subtask} onSingleClick={onClick} />
        <div className="flex-shrink-0 opacity-0 group-hover/strow:opacity-100 transition-opacity ml-auto flex items-center gap-0.5 mr-1">
          {onAddChild && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onAddChild();
              }}
              title="Dodaj podzadanie"
              className="p-0.5 rounded hover:bg-blue-100 dark:hover:bg-blue-900/40 text-gray-400 hover:text-[var(--color-primary)]"
            >
              <Plus size={12} />
            </button>
          )}
          <button
            onClick={handleDelete}
            title="Usuń podzadanie"
            className="p-0.5 rounded hover:bg-red-100 dark:hover:bg-red-900/40 text-gray-400 hover:text-red-600"
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>

      {/* Status */}
      <div className="flex border-r border-gray-200 dark:border-gray-600">
        <StatusSelect subtask={subtask} />
      </div>

      {/* Priority */}
      <div className="flex border-r border-gray-200 dark:border-gray-600">
        <div className="relative w-full">
          <select
            value={subtask.priority}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) =>
              updateSubTask({
                id: subtask.id,
                data: { priority: e.target.value as TaskPriority },
              })
            }
            className={`text-xs font-semibold px-2 py-1 rounded-none border-0 cursor-pointer appearance-none pr-5 w-full text-center ${PRIORITY_COLORS[subtask.priority]}`}
          >
            {(Object.keys(PRIORITY_LABELS) as TaskPriority[]).map((p) => (
              <option key={p} value={p}>
                {PRIORITY_LABELS[p]}
              </option>
            ))}
          </select>
          <ChevronDown
            size={9}
            className="absolute right-1 top-1/2 -translate-y-1/2 pointer-events-none opacity-60"
          />
        </div>
      </div>

      {/* Due week */}
      <div className="px-2 py-1 flex justify-center border-r border-gray-200 dark:border-gray-600">
        {!["done", "cancelled", "n_d"].includes(subtask.status) && wk ? (
          <span className={`text-xs font-medium ${wk.cls}`}>{wk.text}</span>
        ) : (
          <span className="text-xs text-gray-300">—</span>
        )}
      </div>

      {/* Comments */}
      <div className="px-2 py-1 flex justify-center border-r border-gray-200 dark:border-gray-600">
        {subtask.comments_count > 0 ? (
          <div className="flex items-center gap-0.5 text-xs text-gray-400 dark:text-gray-500">
            <MessageSquare size={11} />
            <span>{subtask.comments_count}</span>
          </div>
        ) : (
          <span className="text-xs text-gray-200 dark:text-gray-600">—</span>
        )}
      </div>

      {/* Assignees + quick-add */}
      <div className="px-2 py-1 flex justify-start">
        <SubTaskPeopleCell subtask={subtask} users={users} />
      </div>
    </div>
  );
}

// ── DraggableSubTaskRow ────────────────────────────────────────────────────

interface DraggableSubTaskRowProps {
  subtask: SubTask;
  onClick: () => void;
  onDragStart: (subtask: SubTask | null) => void;
  isDragging: boolean;
  users: InternalUser[];
  onReorder: (
    draggedId: string,
    targetId: string,
    position: "before" | "after",
  ) => void;
  onReparent: (draggedId: string, fromTaskId: string) => void;
  titleIndent?: number;
  onAddChild?: () => void;
  hasChildren?: boolean;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
  isChecked: boolean;
  isParentChecked: boolean;
  onToggleSelect: (id: string) => void;
}

function DraggableSubTaskRow({
  subtask,
  onClick,
  onDragStart,
  isDragging,
  users,
  onReorder,
  onReparent,
  titleIndent = 0,
  onAddChild,
  hasChildren,
  isCollapsed,
  onToggleCollapse,
  isChecked,
  isParentChecked,
  onToggleSelect,
}: DraggableSubTaskRowProps) {
  const [dropIndicator, setDropIndicator] = useState<
    "before" | "after" | "into" | null
  >(null);
  const dropPosRef = useRef<"before" | "after" | "into" | null>(null);

  function calcDropPos(e: React.DragEvent): "before" | "after" | "into" {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientY - rect.top) / rect.height;
    if (pct < 0.40) return "before";
    if (pct > 0.60) return "after";
    return "into";
  }

  return (
    <div
      draggable
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("subtaskId", subtask.id);
        e.dataTransfer.setData("fromTaskId", subtask.task);
        onDragStart(subtask);
      }}
      onDragEnd={() => {
        onDragStart(null!);
        setDropIndicator(null);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        if (e.dataTransfer.types.includes("taskid")) return; // task drags bubble to card
        e.stopPropagation();
        const pos = calcDropPos(e);
        dropPosRef.current = pos;
        if (dropIndicator !== pos) setDropIndicator(pos);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setDropIndicator(null);
          dropPosRef.current = null;
        }
      }}
      onDrop={(e) => {
        const draggedId = e.dataTransfer.getData("subtaskId");
        if (!draggedId) return; // task drops bubble to card
        e.preventDefault();
        e.stopPropagation();
        const pos = dropPosRef.current ?? "after";
        setDropIndicator(null);
        dropPosRef.current = null;
        if (draggedId === subtask.id) return;
        const fromTaskId = e.dataTransfer.getData("fromTaskId");
        if (pos === "into") {
          onReparent(draggedId, fromTaskId);
        } else if (fromTaskId === subtask.task) {
          onReorder(draggedId, subtask.id, pos);
        } else {
          onReparent(draggedId, fromTaskId);
        }
      }}
      style={{ opacity: isDragging ? 0.35 : 1 }}
      className={`pl-4 relative group/row border-b last:border-b-0 border-l-[3px] ${TASK_STATUS_BORDER[subtask.status]} ${
        dropIndicator === "before"
          ? "border-t-[3px] border-t-blue-500 border-b-gray-200 dark:border-b-gray-700"
          : dropIndicator === "after"
            ? "border-b-[3px] border-b-blue-500"
            : dropIndicator === "into"
              ? "border-b-gray-200 dark:border-b-gray-700 bg-[var(--bg-elevated)]/70 dark:bg-blue-900/20 ring-1 ring-inset ring-blue-400"
              : "border-b-gray-200 dark:border-b-gray-700"
      }`}
    >
      <span className="absolute left-0.5 top-1/2 -translate-y-1/2 cursor-grab text-gray-300 hover:text-gray-500 opacity-0 group-hover/row:opacity-100 transition-opacity select-none">
        <GripVertical size={13} />
      </span>
      <TableSubTaskRow
        subtask={subtask}
        onClick={onClick}
        users={users}
        titleIndent={titleIndent}
        onAddChild={onAddChild}
        hasChildren={hasChildren}
        isCollapsed={isCollapsed}
        onToggleCollapse={onToggleCollapse}
        isChecked={isChecked}
        isParentChecked={isParentChecked}
        onToggleSelect={() => onToggleSelect(subtask.id)}
      />
    </div>
  );
}

// ── SubTaskTableTree ───────────────────────────────────────────────────────

interface SubTaskTableTreeProps {
  subtask: SubTask;
  allForTask: SubTask[];
  depth: number;
  taskId: string;
  onOpen: (id: string) => void;
  users: InternalUser[];
  addingChildToId: string | null;
  onStartAdd: (parentId: string) => void;
  onDoneAdd: () => void;
  draggingSubtask: SubTask | null;
  onDragStart: (s: SubTask | null) => void;
  onReorder: (
    draggedId: string,
    targetId: string,
    pos: "before" | "after",
  ) => void;
  onReparent: (
    draggedId: string,
    fromTaskId: string,
    targetSubtaskId: string,
  ) => void;
  forceExpand?: boolean;
  selectedSubtaskIds: Set<string>;
  onToggleSelect: (id: string) => void;
}

function SubTaskTableTree({
  subtask,
  allForTask,
  depth,
  taskId,
  onOpen,
  users,
  addingChildToId,
  onStartAdd,
  onDoneAdd,
  draggingSubtask,
  onDragStart,
  onReorder,
  onReparent,
  forceExpand = false,
  selectedSubtaskIds,
  onToggleSelect,
}: SubTaskTableTreeProps) {
  const [dropPos, setDropPos] = useState<"before" | "after" | "into" | null>(null);
  const dropPosRef = useRef<"before" | "after" | "into" | null>(null);

  function calcChildDropPos(e: React.DragEvent): "before" | "after" | "into" {
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientY - rect.top) / rect.height;
    if (pct < 0.40) return "before";
    if (pct > 0.60) return "after";
    return "into";
  }
  const [isCollapsed, setIsCollapsed] = useState(true);
  const showChildren = forceExpand || !isCollapsed;
  const prevForceExpand = useRef(forceExpand);
  useEffect(() => {
    if (prevForceExpand.current && !forceExpand) setIsCollapsed(true);
    prevForceExpand.current = forceExpand;
  }, [forceExpand]);

  const children = allForTask
    .filter((s) => s.parent === subtask.id)
    .sort(
      (a, b) =>
        a.position - b.position || a.created_at.localeCompare(b.created_at),
    );
  const isAddingHere = addingChildToId === subtask.id;
  const hasChildren = children.length > 0;

  return (
    <>
      {depth === 0 ? (
        <DraggableSubTaskRow
          subtask={subtask}
          isDragging={
            draggingSubtask?.id === subtask.id ||
            (draggingSubtask !== null &&
              selectedSubtaskIds.has(draggingSubtask.id) &&
              selectedSubtaskIds.has(subtask.id))
          }
          onDragStart={onDragStart}
          onClick={() => onOpen(subtask.id)}
          users={users}
          onReorder={onReorder}
          onReparent={(draggedId, fromTaskId) =>
            onReparent(draggedId, fromTaskId, subtask.id)
          }
          titleIndent={0}
          onAddChild={() => onStartAdd(subtask.id)}
          hasChildren={hasChildren}
          isCollapsed={!showChildren}
          onToggleCollapse={() => setIsCollapsed((c: boolean) => !c)}
          isChecked={selectedSubtaskIds.has(subtask.id)}
          isParentChecked={subtask.parent !== null && selectedSubtaskIds.has(subtask.parent)}
          onToggleSelect={onToggleSelect}
        />
      ) : (
        <div
          draggable
          onDragStart={(e) => {
            e.dataTransfer.effectAllowed = "move";
            e.dataTransfer.setData("subtaskId", subtask.id);
            e.dataTransfer.setData("fromTaskId", subtask.task);
            onDragStart(subtask);
          }}
          onDragEnd={() => {
            onDragStart(null!);
            setDropPos(null);
            dropPosRef.current = null;
          }}
          onDragOver={(e) => {
            e.preventDefault();
            if (e.dataTransfer.types.includes("taskid")) return; // task drags bubble to card
            e.stopPropagation();
            const pos = calcChildDropPos(e);
            dropPosRef.current = pos;
            if (dropPos !== pos) setDropPos(pos);
          }}
          onDragLeave={(e) => {
            if (!e.currentTarget.contains(e.relatedTarget as Node)) {
              setDropPos(null);
              dropPosRef.current = null;
            }
          }}
          onDrop={(e) => {
            const draggedId = e.dataTransfer.getData("subtaskId");
            if (!draggedId) return; // task drops bubble to card
            e.preventDefault();
            e.stopPropagation();
            const pos = dropPosRef.current ?? "after";
            setDropPos(null);
            dropPosRef.current = null;
            if (draggedId === subtask.id) return;
            const fromTaskId = e.dataTransfer.getData("fromTaskId");
            if (pos === "into") {
              onReparent(draggedId, fromTaskId, subtask.id);
            } else if (fromTaskId === subtask.task) {
              onReorder(draggedId, subtask.id, pos);
            } else {
              onReparent(draggedId, fromTaskId, subtask.id);
            }
          }}
          style={{
            opacity:
              draggingSubtask?.id === subtask.id ||
              (draggingSubtask !== null &&
                selectedSubtaskIds.has(draggingSubtask.id) &&
                selectedSubtaskIds.has(subtask.id))
                ? 0.35
                : 1,
          }}
          className={`relative border-b last:border-b-0 cursor-grab transition-colors ${
            dropPos === "before"
              ? "border-t-[3px] border-t-blue-500 border-b-gray-100 dark:border-b-gray-700/50"
              : dropPos === "after"
                ? "border-b-[3px] border-b-blue-500 border-t-transparent"
                : dropPos === "into"
                  ? "bg-blue-50/70 dark:bg-blue-900/20 ring-1 ring-inset ring-blue-400 border-blue-200 dark:border-blue-700"
                  : "border-gray-100 dark:border-gray-700/50"
          }`}
        >
          <TableSubTaskRow
            subtask={subtask}
            onClick={() => onOpen(subtask.id)}
            users={users}
            titleIndent={depth * 24}
            onAddChild={() => onStartAdd(subtask.id)}
            hasChildren={hasChildren}
            isCollapsed={!showChildren}
            onToggleCollapse={() => setIsCollapsed((c: boolean) => !c)}
            isChecked={selectedSubtaskIds.has(subtask.id)}
            isParentChecked={subtask.parent !== null && selectedSubtaskIds.has(subtask.parent)}
            onToggleSelect={() => onToggleSelect(subtask.id)}
          />
        </div>
      )}
      {showChildren && children.map((child) => (
        <SubTaskTableTree
          key={child.id}
          subtask={child}
          allForTask={allForTask}
          depth={depth + 1}
          taskId={taskId}
          onOpen={onOpen}
          users={users}
          addingChildToId={addingChildToId}
          onStartAdd={onStartAdd}
          onDoneAdd={onDoneAdd}
          draggingSubtask={draggingSubtask}
          onDragStart={onDragStart}
          onReorder={onReorder}
          onReparent={onReparent}
          forceExpand={forceExpand}
          selectedSubtaskIds={selectedSubtaskIds}
          onToggleSelect={onToggleSelect}
        />
      ))}
      {isAddingHere && (
        <InlineSubTaskAdd
          taskId={taskId}
          parentId={subtask.id}
          onDone={onDoneAdd}
          depth={depth + 1}
        />
      )}
    </>
  );
}

// ── Task row color palette ─────────────────────────────────────────────────

function stableTaskColor(id: string, palette: string[]): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
}

const TASK_ROW_COLORS = [
  "#f43f5e", // rose-500
  "#ea580c", // orange-600
  "#7c3aed", // violet-600
  "#16a34a", // green-600
  "#2563eb", // blue-600
  "#db2777", // pink-600
  "#0d9488", // teal-600
  "#d97706", // amber-600
  "#4f46e5", // indigo-600
  "#0891b2", // cyan-600
];

// ── TaskTitleCell ──────────────────────────────────────────────────────────

function TaskTitleCell({
  task,
  color,
  onSingleClick,
}: {
  task: Task;
  color?: string;
  onSingleClick?: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(task.title);
  const { mutate: updateTask } = useUpdateTask(task.id);
  const inputRef = useRef<HTMLInputElement>(null);
  const clickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setValue(task.title);
  }, [task.title]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  function save() {
    const trimmed = value.trim();
    if (trimmed && trimmed !== task.title) updateTask({ title: trimmed });
    else setValue(task.title);
    setEditing(false);
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            save();
          }
          if (e.key === "Escape") {
            setValue(task.title);
            setEditing(false);
          }
        }}
        onClick={(e) => e.stopPropagation()}
        style={color ? { color } : undefined}
        className="flex-1 text-sm font-bold uppercase bg-white dark:bg-gray-700 border border-[var(--color-primary)] rounded px-2 py-0.5 focus:outline-none min-w-0"
      />
    );
  }

  return (
    <span
      style={color ? { color } : undefined}
      className="flex-1 text-sm font-bold uppercase cursor-pointer select-none truncate hover:underline"
      title="Kliknij, aby otworzyć szczegóły; dwukrotnie, aby edytować"
      onClick={(e) => {
        e.stopPropagation();
        if (clickTimer.current) {
          clearTimeout(clickTimer.current);
          clickTimer.current = null;
          setEditing(true);
        } else {
          clickTimer.current = setTimeout(() => {
            clickTimer.current = null;
            onSingleClick?.();
          }, 200);
        }
      }}
    >
      {task.title}
    </span>
  );
}

// ── TaskPeopleCell ─────────────────────────────────────────────────────────

function TaskPeopleCell({
  task,
  users,
}: {
  task: Task;
  users: InternalUser[];
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [dropPos, setDropPos] = useState({ top: 0, right: 0 });
  const btnRef = useRef<HTMLButtonElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);
  const { mutate: assign } = useAssignUser(task.id);
  const { mutate: unassign } = useUnassignUser(task.id);

  useEffect(() => {
    if (!open) return;
    function onOutside(e: MouseEvent) {
      if (
        dropRef.current &&
        !dropRef.current.contains(e.target as Node) &&
        btnRef.current &&
        !btnRef.current.contains(e.target as Node)
      )
        setOpen(false);
    }
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, [open]);

  const DROPDOWN_H = 280;

  function openDropdown(e: React.MouseEvent) {
    e.stopPropagation();
    if (open) {
      setOpen(false);
      return;
    }
    const rect = btnRef.current!.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const top =
      spaceBelow >= DROPDOWN_H
        ? rect.bottom + 4
        : Math.max(8, rect.top - DROPDOWN_H - 4);
    setDropPos({ top, right: window.innerWidth - rect.right });
    setSearch("");
    setOpen(true);
  }

  const assignedIds = new Set(task.assignees.map((a) => a.id));
  const filtered = users
    .filter((u) => u.full_name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => a.full_name.localeCompare(b.full_name, "pl"));

  return (
    <div
      className="flex items-center gap-1 flex-shrink-0"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex -space-x-1.5">
        {task.assignees.slice(0, 3).map((a) => (
          <UserAvatar
            key={a.id}
            userId={a.id}
            fullName={a.full_name}
            profilePictureUrl={a.profile_picture_url}
            size="sm"
            border
          />
        ))}
        {task.assignees.length > 3 && (
          <div className="w-5 h-5 rounded-full bg-gray-200 dark:bg-gray-600 text-gray-500 dark:text-gray-400 text-[10px] flex items-center justify-center border-2 border-white dark:border-gray-800 font-medium">
            +{task.assignees.length - 3}
          </div>
        )}
      </div>
      <button
        ref={btnRef}
        onClick={openDropdown}
        className="w-5 h-5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-400 hover:bg-blue-100 dark:hover:bg-blue-900/40 hover:text-[var(--color-primary)] flex items-center justify-center transition-colors flex-shrink-0"
        title="Zarządzaj osobami"
      >
        <Plus size={10} />
      </button>
      {open &&
        createPortal(
          <div
            ref={dropRef}
            style={{
              top: dropPos.top,
              right: dropPos.right,
              position: "fixed",
            }}
            className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-xl z-[200] w-52"
          >
            <div className="p-2 border-b border-gray-100 dark:border-gray-700">
              <input
                autoFocus
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onMouseDown={(e) => e.stopPropagation()}
                placeholder="Szukaj osoby..."
                className="w-full text-xs px-2 py-1.5 rounded focus:outline-none focus:ring-1 focus:ring-[var(--color-primary)] bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 placeholder:text-gray-400"
              />
            </div>
            <div className="max-h-48 overflow-y-auto py-1">
              {filtered.map((u) => {
                const assigned = assignedIds.has(u.id);
                return (
                  <button
                    key={u.id}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      assigned ? unassign(u.id) : assign(u.id);
                    }}
                    className="w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 hover:bg-[var(--color-primary-dim)] dark:hover:bg-blue-900/30 transition-colors"
                  >
                    <UserAvatar
                      fullName={u.full_name}
                      profilePictureUrl={u.profile_picture_url}
                      size="xs"
                    />
                    <span
                      className={`flex-1 truncate ${assigned ? "text-gray-900 dark:text-gray-100 font-medium" : "text-gray-600 dark:text-gray-400"}`}
                    >
                      {u.full_name}
                    </span>
                    {assigned && (
                      <Check
                        size={12}
                        className="text-[var(--color-primary)] flex-shrink-0"
                      />
                    )}
                  </button>
                );
              })}
              {filtered.length === 0 && (
                <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-3">
                  Brak wyników
                </p>
              )}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

// ── DroppableTaskZone ──────────────────────────────────────────────────────

interface DroppableTaskZoneProps {
  taskId: string;
  children: React.ReactNode;
  isOver: boolean;
  onDragOver: (taskId: string) => void;
  onDrop: (taskId: string, e: React.DragEvent) => void;
}

function DroppableTaskZone({
  taskId,
  children,
  isOver,
  onDragOver,
  onDrop,
}: DroppableTaskZoneProps) {
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        onDragOver(taskId);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) onDragOver("");
      }}
      onDrop={(e) => {
        e.preventDefault();
        onDrop(taskId, e);
      }}
      className={`rounded-xl transition-colors ${isOver ? "ring-2 ring-blue-300 ring-inset" : ""}`}
    >
      {children}
    </div>
  );
}

// ── InlineSubTaskAdd ───────────────────────────────────────────────────────

function InlineSubTaskAdd({
  taskId,
  parentId,
  onDone,
  depth = 0,
}: {
  taskId: string;
  parentId?: string | null;
  onDone: () => void;
  depth?: number;
}) {
  const [title, setTitle] = useState("");
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [dueDate, setDueDate] = useState<string | null>(null);
  const { mutate: createSubTask, isPending } = useCreateSubTask(taskId);
  const { data: rawUsers = [] } = useInternalUsers({ includeFridayContractors: true, sameBranch: true });
  const allUsers = [...rawUsers].sort((a, b) =>
    a.full_name.localeCompare(b.full_name, "pl"),
  );
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  function toggleUser(id: string) {
    setSelectedUsers((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function submit() {
    const t = title.trim();
    if (!t) {
      onDone();
      return;
    }
    createSubTask(
      {
        title: t,
        assignee_ids: selectedUsers,
        due_date: dueDate,
        parent: parentId ?? null,
      },
      {
        onSuccess: () => {
          setTitle("");
          setSelectedUsers([]);
          setDueDate(null);
          onDone();
        },
      },
    );
  }

  return (
    <div
      className="border-b border-gray-100 dark:border-gray-700/60 bg-[var(--bg-elevated)]/50 dark:bg-blue-900/10"
      style={{ paddingLeft: `${20 + depth * 24}px` }}
    >
      <div className="px-4 py-2 flex flex-col gap-2">
        <input
          ref={inputRef}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              submit();
            }
            if (e.key === "Escape") onDone();
          }}
          disabled={isPending}
          placeholder="Nazwa podzadania… Enter aby zapisać, Esc aby anulować"
          className="w-full text-sm px-2 py-1.5 border border-blue-300 rounded focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] bg-white dark:bg-gray-800"
        />
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
            Przypisz:
          </span>
          {allUsers.map((u) => {
            const active = selectedUsers.includes(u.id);
            return (
              <button
                key={u.id}
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  toggleUser(u.id);
                }}
                title={u.full_name}
                className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border transition-colors ${
                  active
                    ? "bg-[var(--color-primary)] text-white border-[var(--color-primary)]"
                    : "bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-300 hover:border-[var(--color-primary)] hover:text-[var(--color-primary)]"
                }`}
              >
                <UserAvatar
                  userId={u.id}
                  fullName={u.full_name}
                  profilePictureUrl={u.profile_picture_url}
                  size="xs"
                  initialsClassName={
                    active
                      ? "bg-white dark:bg-gray-800/20 text-white font-bold"
                      : "bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 font-bold"
                  }
                />
                {u.full_name}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
            Termin:
          </span>
          <WeekSelect
            value={dueDate}
            onChange={setDueDate}
            placeholder="— brak —"
            pastWeeks={0}
          />
        </div>
      </div>
    </div>
  );
}

// ── Mobile subtask threaded tree ───────────────────────────────────────────

function MobileSubtaskItem({
  st,
  subs,
  depth,
  taskId,
  onOpen,
  isLast,
  forceExpand = false,
}: {
  st: SubTask;
  subs: SubTask[];
  depth: number;
  taskId: string;
  onOpen: (taskId: string, subtaskId: string) => void;
  isLast: boolean;
  forceExpand?: boolean;
}) {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const hasChildren = st.children_count > 0;
  const showChildren = forceExpand || !isCollapsed;
  const prevForceExpand = useRef(forceExpand);
  useEffect(() => {
    if (prevForceExpand.current && !forceExpand) setIsCollapsed(true);
    prevForceExpand.current = forceExpand;
  }, [forceExpand]);

  return (
    <div className={depth === 0 && !isLast ? "border-b border-gray-100 dark:border-gray-700/60" : ""}>
      <div
        className="w-full flex items-center gap-2 py-2.5 pr-3 hover:bg-gray-50 dark:hover:bg-gray-700/40 transition-colors"
        style={{ paddingLeft: depth === 0 ? 16 : 10 }}
      >
        {hasChildren ? (
          <button
            onClick={() => setIsCollapsed((c: boolean) => !c)}
            className="flex-shrink-0 text-gray-400 dark:text-gray-500"
          >
            {showChildren ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
          </button>
        ) : (
          <span className="w-3 flex-shrink-0" />
        )}
        <button
          className="flex-1 flex items-center gap-2 text-left min-w-0"
          onClick={() => onOpen(taskId, st.id)}
        >
          <span className="flex-1 text-xs text-gray-700 dark:text-gray-300 truncate min-w-0">{st.title}</span>
          <span className={`px-1.5 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ${TASK_STATUS_COLORS[st.status]}`}>
            {TASK_STATUS_LABELS[st.status]}
          </span>
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${PRIORITY_DOT[st.priority]}`} title={PRIORITY_LABELS[st.priority]} />
        </button>
      </div>
      {showChildren && hasChildren && (
        <MobileSubtaskGroup subs={subs} parentId={st.id} depth={depth + 1} taskId={taskId} onOpen={onOpen} forceExpand={forceExpand} />
      )}
    </div>
  );
}

function MobileSubtaskGroup({
  subs,
  parentId,
  depth,
  taskId,
  onOpen,
  forceExpand = false,
}: {
  subs: SubTask[];
  parentId: string | null;
  depth: number;
  taskId: string;
  onOpen: (taskId: string, subtaskId: string) => void;
  forceExpand?: boolean;
}) {
  const children = subs
    .filter((s) => (s.parent ?? null) === parentId)
    .sort((a, b) => a.position - b.position || a.created_at.localeCompare(b.created_at));

  if (children.length === 0) return null;

  const rows = children.map((st, idx) => (
    <MobileSubtaskItem
      key={st.id}
      st={st}
      subs={subs}
      depth={depth}
      taskId={taskId}
      onOpen={onOpen}
      isLast={idx === children.length - 1}
      forceExpand={forceExpand}
    />
  ));

  if (depth === 0) return <>{rows}</>;

  return (
    <div className="ml-2 border-l-2 border-gray-200 dark:border-gray-600">
      {rows}
    </div>
  );
}

// ── TasksBoardPage ─────────────────────────────────────────────────────────

function collectDescendantIds(subtaskId: string, allSubs: SubTask[]): string[] {
  const ids: string[] = [];
  const queue = [subtaskId];
  while (queue.length) {
    const id = queue.shift()!;
    allSubs.filter((s) => s.parent === id).forEach((c) => {
      ids.push(c.id);
      queue.push(c.id);
    });
  }
  return ids;
}

export function TasksBoardPage() {
  const currentUser = useAuthStore((s) => s.user);
  const [searchParams, setSearchParams] = useSearchParams();

  const [filters, setFilters] = useState<SubTaskFilters>(() => {
    const saved = readFridayBoardSession();
    if (saved) {
      const { _searchInput: _si, _userSearch: _us, _showArchived: _sa, ...rest } = saved;
      return rest;
    }
    return { assigned_to: currentUser ? [currentUser.id] : [] };
  });
  const [searchInput, setSearchInput] = useState<string>(() => {
    const saved = readFridayBoardSession();
    return saved?._searchInput ?? "";
  });
  const [userSearch, setUserSearch] = useState<string>(() => readFridayBoardSession()?._userSearch ?? "");
  const [showArchived, setShowArchived] = useState<boolean>(() => readFridayBoardSession()?._showArchived ?? false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((f) => ({ ...f, search: searchInput || undefined }));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    try {
      sessionStorage.setItem(FRIDAY_BOARD_SESSION_KEY, JSON.stringify({ ...filters, _searchInput: searchInput, _userSearch: userSearch, _showArchived: showArchived }));
    } catch { /* ignore */ }
  }, [filters, searchInput, userSearch, showArchived]);

  const { data: uiPrefs } = useTaskUIPrefs();
  const { mutate: setUIPrefs } = useSetTaskUIPrefs();
  const filtersHidden = uiPrefs?.filters_hidden ?? false;
  const kanbanHidden = uiPrefs?.kanban_hidden ?? false;
  const tablePanelHidden = uiPrefs?.table_panel_hidden ?? false;

  const [showFilters, setShowFilters] = useState(true);
  const [selectedSubTask, setSelectedSubTask] = useState<{
    taskId: string;
    subtaskId: string;
  } | null>(null);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [taskInitialTab, setTaskInitialTab] = useState<"comments" | undefined>(undefined);
  const [targetCommentId, setTargetCommentId] = useState<string | null>(null);
  const [createStatus, setCreateStatus] = useState<TaskStatus | null>(null);
  const [createTaskOpen, setCreateTaskOpen] = useState(false);
  const [addingToTask, setAddingToTask] = useState<string | null>(null);
  const [addingChildToId, setAddingChildToId] = useState<string | null>(null);
  const [draggingSubtask, setDraggingSubtask] = useState<SubTask | null>(null);
  const [dragOverTaskId, setDragOverTaskId] = useState<string>("");
  const [selectedSubtaskIds, setSelectedSubtaskIds] = useState<Set<string>>(new Set());

  function handleToggleSelect(subtaskId: string) {
    setSelectedSubtaskIds((prev) => {
      const next = new Set(prev);
      if (next.has(subtaskId)) {
        next.delete(subtaskId);
        collectDescendantIds(subtaskId, allSubtasksForTable).forEach((id) => next.delete(id));
      } else {
        next.add(subtaskId);
        collectDescendantIds(subtaskId, allSubtasksForTable).forEach((id) => next.add(id));
      }
      return next;
    });
  }

  // Open task/subtask from notification URL (?task=X or ?task=X&subtask=Y) and clear person filter
  useEffect(() => {
    const taskId = searchParams.get("task");
    const subtaskId = searchParams.get("subtask");
    if (taskId && subtaskId) {
      setSelectedSubTask({ taskId, subtaskId });
      setFilters({ assigned_to: [] });
      setSearchParams({}, { replace: true });
    } else if (taskId) {
      const section = searchParams.get("section");
      const comment = searchParams.get("comment");
      setSelectedTaskId(taskId);
      setTaskInitialTab(section === "comments" || comment ? "comments" : undefined);
      setTargetCommentId(comment ?? null);
      setFilters({ assigned_to: [] });
      setSearchParams({}, { replace: true });
    }
  }, [searchParams]);

  const [taskCategoryFilter, setTaskCategoryFilter] = useState<string | "none" | undefined>(undefined);

  const projectFilter = filters.project?.length
    ? { project: filters.project }
    : undefined;

  const taskFilter: TaskFilters = {
    ...projectFilter,
    ...(taskCategoryFilter === "none"
      ? { no_category: true }
      : taskCategoryFilter
        ? { category: taskCategoryFilter }
        : {}),
    ...(showArchived ? { include_archived: true } : {}),
  };

  const subtaskFilter: SubTaskFilters = { ...filters, ...(showArchived ? { include_archived: true } : {}) };
  const tableSubtaskFilter: SubTaskFilters | undefined = showArchived
    ? { ...(projectFilter ?? {}), include_archived: true }
    : projectFilter;

  const { data: subtasks = [], isLoading } = useAllSubTasks(subtaskFilter);
  const { data: allSubtasksForTable = [] } = useAllSubTasks(tableSubtaskFilter);
  const { data: projects = [] } = useProjects();
  const { data: allTasks = [] } = useTasks(taskFilter);
  const { data: categories = [] } = useTaskCategories();
  // Assignment candidates only (people cells, create-subtask modal) — branch-scoped.
  // The Osoby filter fetches its own company-wide list via UserMultiSelect.
  const { data: users = [] } = useInternalUsers({ includeFridayContractors: true, sameBranch: true });
  const { data: clients = [] } = useClientsForTasks();
  const { mutate: reorderSubTasks } = useReorderSubTasks();
  const { mutate: reparentSubTask } = useReparentSubTask();
  const { mutate: reorderTasks } = useReorderTasks();
  const { mutate: deleteTask } = useDeleteTask();
  const { mutate: pinTask } = usePinTask();
  const { mutate: archiveTask, isPending: isArchiving } = useArchiveTask();
  // Task pending archive confirmation (shown in a modal).
  const [taskToArchive, setTaskToArchive] = useState<Task | null>(null);

  const dragTaskRef = useRef<string | null>(null);
  const taskDropPosRef = useRef<"before" | "after" | null>(null);
  const hasLoadedOnce = useRef(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const lastDragY = useRef(0);
  const scrollRafRef = useRef<number | null>(null);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;
    const THRESHOLD = 80;
    const SPEED = 14;

    const onDragOver = (e: DragEvent) => {
      lastDragY.current = e.clientY;
    };

    let running = false;
    const tick = () => {
      if (!running) return;
      const el = scrollContainerRef.current;
      if (el) {
        const rect = el.getBoundingClientRect();
        const y = lastDragY.current;
        const fromTop = y - rect.top;
        const fromBot = rect.bottom - y;
        if (fromTop > 0 && fromTop < THRESHOLD) {
          el.scrollTop -= SPEED * (1 - fromTop / THRESHOLD);
        } else if (fromBot > 0 && fromBot < THRESHOLD) {
          el.scrollTop += SPEED * (1 - fromBot / THRESHOLD);
        }
      }
      scrollRafRef.current = requestAnimationFrame(tick);
    };

    const onDragStart = () => {
      running = true;
      scrollRafRef.current = requestAnimationFrame(tick);
    };
    const onDragEnd = () => {
      running = false;
      if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
    };

    container.addEventListener("dragover", onDragOver);
    document.addEventListener("dragstart", onDragStart);
    document.addEventListener("dragend", onDragEnd);
    return () => {
      container.removeEventListener("dragover", onDragOver);
      document.removeEventListener("dragstart", onDragStart);
      document.removeEventListener("dragend", onDragEnd);
      if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
    };
  }, []);
  const [taskDropIndicator, setTaskDropIndicator] = useState<{ id: string; pos: "before" | "after" } | null>(null);

  const [collapsedTasks, setCollapsedTasks] = useState<Set<string>>(new Set());
  const collapsedTasksInitialized = useRef(false);
  const [expandAllSet, setExpandAllSet] = useState<Set<string>>(new Set());
  const [cloneOpen, setCloneOpen] = useState(false);
  const [mobileKanbanStatus, setMobileKanbanStatus] = useState<TaskStatus>("todo_now");

  useEffect(() => {
    if (!collapsedTasksInitialized.current && allTasks.length > 0) {
      collapsedTasksInitialized.current = true;
      setCollapsedTasks(new Set(allTasks.map((t) => t.id)));
    }
  }, [allTasks]);

  function toggleExpand(taskId: string) {
    setCollapsedTasks((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
        setExpandAllSet((p) => { const n = new Set(p); n.delete(taskId); return n; });
      }
      return next;
    });
  }

  function toggleExpandAll(taskId: string) {
    setExpandAllSet((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
        setCollapsedTasks((c) => { const n = new Set(c); n.add(taskId); return n; });
      } else {
        next.add(taskId);
        setCollapsedTasks((c) => { const n = new Set(c); n.delete(taskId); return n; });
      }
      return next;
    });
  }

  const subtasksByTaskId = allSubtasksForTable.reduce<
    Record<string, SubTask[]>
  >((acc, st) => {
    if (!acc[st.task]) acc[st.task] = [];
    acc[st.task].push(st);
    return acc;
  }, {});

  const subtasksByStatus = TASK_STATUS_ORDER.reduce<
    Record<TaskStatus, typeof subtasks>
  >(
    (acc, s) => ({ ...acc, [s]: [] }),
    {} as Record<TaskStatus, typeof subtasks>,
  );
  subtasks.forEach((st) => {
    if (subtasksByStatus[st.status]) subtasksByStatus[st.status].push(st);
  });

  const filteredTasks = useMemo(() => {
    const selectedUsers = filters.assigned_to;
    if (!selectedUsers || selectedUsers.length === 0) return allTasks;
    const hasMatch = (task: (typeof allTasks)[number]) => {
      if (task.assignees.some((a) => selectedUsers.includes(a.id))) return true;
      return (subtasksByTaskId[task.id] ?? []).some((s) =>
        s.assignees.some((a) => selectedUsers.includes(a.id)),
      );
    };
    return [...allTasks].sort((a, b) => Number(hasMatch(b)) - Number(hasMatch(a)));
  }, [allTasks, filters.assigned_to, subtasksByTaskId]);

  const tasksForModal = allTasks.map((t) => ({
    id: t.id,
    title: t.title,
    project_name: t.project_name,
  }));

  const hasActiveFilters =
    searchInput ||
    filters.search ||
    filters.priority ||
    filters.status ||
    (filters.project && filters.project.length > 0) ||
    (filters.client && filters.client.length > 0) ||
    (filters.assigned_to && filters.assigned_to.length > 0) ||
    taskCategoryFilter !== undefined;

  function getSelectionRoots(): SubTask[] {
    return [...selectedSubtaskIds]
      .map((id) => allSubtasksForTable.find((s) => s.id === id))
      .filter((s): s is SubTask => s !== undefined && !selectedSubtaskIds.has(s.parent ?? ""));
  }

  function handleDrop(targetTaskId: string, e: React.DragEvent) {
    setDragOverTaskId("");
    setDraggingSubtask(null);
    const fromTaskId = e.dataTransfer.getData("fromTaskId");
    const subtaskId = e.dataTransfer.getData("subtaskId");
    if (!subtaskId || !fromTaskId) return;

    if (selectedSubtaskIds.has(subtaskId)) {
      getSelectionRoots().forEach((root) => {
        if (root.task === targetTaskId && root.parent === null) return;
        reparentSubTask({ taskId: root.task, subtaskId: root.id, parent_id: null, task_id: targetTaskId });
      });
      setSelectedSubtaskIds(new Set());
    } else {
      const dragged = allSubtasksForTable.find((s) => s.id === subtaskId);
      if (dragged?.parent || fromTaskId !== targetTaskId) {
        reparentSubTask({ taskId: fromTaskId, subtaskId, parent_id: null, task_id: targetTaskId });
      }
    }
  }

  function handleReparent(
    draggedId: string,
    fromTaskId: string,
    targetSubtaskId: string,
  ) {
    const targetSub = allSubtasksForTable.find((s) => s.id === targetSubtaskId);
    if (!targetSub) return;

    if (selectedSubtaskIds.has(draggedId)) {
      getSelectionRoots().forEach((root) => {
        if (root.id === targetSubtaskId) return;
        reparentSubTask({ taskId: root.task, subtaskId: root.id, parent_id: targetSubtaskId, task_id: targetSub.task });
      });
      setSelectedSubtaskIds(new Set());
    } else {
      reparentSubTask({ taskId: fromTaskId, subtaskId: draggedId, parent_id: targetSubtaskId, task_id: targetSub.task });
    }
  }

  function handleTaskRowDrop(e: React.DragEvent, targetTaskId: string, position: "before" | "after") {
    if (e.dataTransfer.getData("subtaskId")) return;
    const fromId = e.dataTransfer.getData("taskId") || dragTaskRef.current;
    if (!fromId || fromId === targetTaskId) return;
    const ids = allTasks.map((t) => t.id);
    const fromIdx = ids.indexOf(fromId);
    const toIdx = ids.indexOf(targetTaskId);
    if (fromIdx === -1 || toIdx === -1) return;
    const newIds = [...ids];
    newIds.splice(fromIdx, 1);
    const insertIdx = newIds.indexOf(targetTaskId);
    newIds.splice(position === "before" ? insertIdx : insertIdx + 1, 0, fromId);
    reorderTasks(newIds);
    dragTaskRef.current = null;
    taskDropPosRef.current = null;
    setTaskDropIndicator(null);
  }

  function handleReorder(
    draggedId: string,
    targetId: string,
    position: "before" | "after",
  ) {
    const draggedSub = allSubtasksForTable.find((s) => s.id === draggedId);
    const targetSub = allSubtasksForTable.find((s) => s.id === targetId);
    if (!draggedSub || !targetSub) return;
    if (draggedSub.task !== targetSub.task) return;
    const taskId = draggedSub.task;

    // Cross-parent drop: reparent dragged to be a sibling of target
    if (draggedSub.parent !== targetSub.parent) {
      reparentSubTask({
        taskId,
        subtaskId: draggedId,
        parent_id: targetSub.parent,
        task_id: taskId,
      });
      return;
    }

    // Same parent: reorder among siblings only
    const siblings = allSubtasksForTable
      .filter((s) => s.task === taskId && s.parent === draggedSub.parent)
      .sort((a, b) => a.position - b.position || a.created_at.localeCompare(b.created_at));
    const fromIdx = siblings.findIndex((s) => s.id === draggedId);
    if (fromIdx === -1) return;
    const [removed] = siblings.splice(fromIdx, 1);
    const targetIdx = siblings.findIndex((s) => s.id === targetId);
    if (targetIdx === -1) return;
    siblings.splice(position === "before" ? targetIdx : targetIdx + 1, 0, removed);
    reorderSubTasks({ taskId, ids: siblings.map((s) => s.id) });
  }

  if (!isLoading) hasLoadedOnce.current = true;
  if (isLoading && !hasLoadedOnce.current) return <PageLoader />;

  return (
    <div className="flex flex-col gap-4 overflow-y-auto sm:h-full sm:overflow-hidden">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 flex-shrink-0">
        <div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Friday
          </h1>
            <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-0.5" />
            <p className="text-xs text-[var(--color-primary)] font-medium mt-1">
              {currentUser?.is_ceo || currentUser?.role === "hr" || currentUser?.role === "accountant"
                ? "Wszystkie oddziały"
                : currentUser?.branch?.name
                ? `Oddział: ${currentUser.branch.name}`
                : null}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setCreateTaskOpen(true)}
            className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 text-gray-700 dark:text-gray-300 text-sm rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50"
          >
            <Plus size={16} />
            <span className="hidden sm:inline">Nowe zadanie</span>
          </button>
          <button
            onClick={() => setCreateStatus("todo_now")}
            className="flex items-center gap-2 px-3 py-2 bg-[var(--color-primary)] text-white text-sm rounded-lg hover:bg-[var(--color-primary-hover)]"
          >
            <Plus size={16} />
            <span className="hidden sm:inline">Nowe podzadanie</span>
          </button>
          <button
            onClick={() => setCloneOpen(true)}
            className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50"
          >
            <Copy size={16} />
            <span className="hidden sm:inline">Klonuj do projektu</span>
          </button>
          <button
            onClick={() => setUIPrefs({ kanban_hidden: !kanbanHidden })}
            title={kanbanHidden ? "Pokaż kanban" : "Ukryj kanban"}
            className={`flex items-center gap-1.5 px-3 py-2 border text-sm rounded-lg transition-colors ${
              kanbanHidden
                ? "bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400"
                : "bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50"
            }`}
          >
            {kanbanHidden ? <Eye size={15} /> : <EyeOff size={15} />}
            <span className="hidden sm:inline">{kanbanHidden ? "Pokaż kanban" : "Ukryj kanban"}</span>
          </button>
          <button
            onClick={() => setUIPrefs({ filters_hidden: !filtersHidden })}
            title={filtersHidden ? "Pokaż filtry" : "Ukryj filtry"}
            className={`flex items-center gap-1.5 px-3 py-2 border text-sm rounded-lg transition-colors ${
              filtersHidden
                ? "bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400"
                : "bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50"
            }`}
          >
            {filtersHidden ? <Eye size={15} /> : <EyeOff size={15} />}
            <span className="hidden sm:inline">{filtersHidden ? "Pokaż filtry" : "Ukryj filtry"}</span>
          </button>
          <button
            onClick={() => setUIPrefs({ table_panel_hidden: !tablePanelHidden })}
            title={tablePanelHidden ? "Pokaż panel zadań" : "Ukryj panel zadań"}
            className={`flex items-center gap-1.5 px-3 py-2 border text-sm rounded-lg transition-colors ${
              tablePanelHidden
                ? "bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400"
                : "bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50"
            }`}
          >
            {tablePanelHidden ? <PanelBottomOpen size={15} /> : <PanelBottomClose size={15} />}
            <span className="hidden sm:inline">{tablePanelHidden ? "Pokaż zadania" : "Ukryj zadania"}</span>
          </button>
          <label
            title="Pokaż zarchiwizowane zadania"
            className={`flex items-center gap-1.5 px-3 py-2 border text-sm rounded-lg cursor-pointer transition-colors ${
              showArchived
                ? "bg-amber-50 dark:bg-amber-900/20 border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-400"
                : "bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50"
            }`}
          >
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="w-3.5 h-3.5 rounded border-gray-300 dark:border-gray-600 accent-amber-500"
            />
            <Archive size={15} />
            <span className="hidden sm:inline">Pokaż zarchiwizowane</span>
          </label>
        </div>
      </div>

      {/* Postęp prac — widoki przeniesione z menu bocznego (badge rozdzielone). */}
      <WorkProgressSection />

      {/* Filters */}
      {!filtersHidden && (
      <div className="flex flex-col gap-2 flex-shrink-0">
        {/* Mobile toggle */}
        <div className="sm:hidden flex items-center justify-between">
          <button
            onClick={() => setShowFilters((v) => !v)}
            className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <SlidersHorizontal size={15} />
            <span>Filtry</span>
            {hasActiveFilters && (
              <span className="w-2 h-2 rounded-full bg-[var(--color-primary)] inline-block" />
            )}
            <ChevronDown
              size={14}
              className={`transition-transform ${showFilters ? "rotate-180" : ""}`}
            />
          </button>
          {hasActiveFilters && (
            <button
              onClick={() => {
                setFilters({ assigned_to: currentUser ? [currentUser.id] : [] });
                setSearchInput("");
                setUserSearch("");
                setTaskCategoryFilter(undefined);
              }}
              className="text-xs text-gray-400 dark:text-gray-500 hover:text-gray-700"
            >
              Resetuj
            </button>
          )}
        </div>

        {/* Filter body — always visible on sm+, toggleable on mobile */}
        <div className={`flex flex-col gap-2 ${showFilters ? "" : "hidden sm:flex"}`}>
          <div className="flex gap-3 flex-wrap items-center">
            <input
              placeholder="Szukaj..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] focus:border-[var(--color-primary)] w-48"
            />
            <select
              value={filters.priority ?? ""}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  priority: (e.target.value as TaskPriority) || undefined,
                }))
              }
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
            >
              <option value="">Priorytet: wszystkie</option>
              <option value="high">Wysoki</option>
              <option value="medium">Średni</option>
              <option value="low">Niski</option>
            </select>
            <SearchableSelect
              placeholder="Przedsięwzięcie: wszystkie"
              value={filters.project?.[0] ?? ""}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  project: e.target.value ? [e.target.value] : [],
                }))
              }
              options={projects.map((p) => ({
                value: p.id,
                label: `${p.short_name} – ${p.name}`,
              }))}
            />
            <SearchableSelect
              placeholder="Klient: wszyscy"
              value={filters.client?.[0] ?? ""}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  client: e.target.value ? [e.target.value] : [],
                }))
              }
              options={clients.map((c) => ({ value: c.id, label: c.name }))}
            />
            <CategoryFilter
              categories={categories}
              activeFilter={taskCategoryFilter}
              onFilterChange={setTaskCategoryFilter}
            />
            {hasActiveFilters && (
              <button
                onClick={() => {
                  setFilters({ assigned_to: currentUser ? [currentUser.id] : [] });
                  setSearchInput("");
                  setUserSearch("");
                  setTaskCategoryFilter(undefined);
                }}
                className="hidden sm:inline text-sm text-gray-400 dark:text-gray-500 hover:text-gray-700 px-1"
              >
                Resetuj filtry
              </button>
            )}
          </div>

          <UserMultiSelect
            selected={filters.assigned_to ?? []}
            onChange={(ids) =>
              setFilters((f) => ({ ...f, assigned_to: ids.length ? ids : [] }))
            }
            userSearch={userSearch}
            onUserSearchChange={setUserSearch}
          />
        </div>
      </div>
      )}

      {/* Scrollable main content */}
      <div ref={scrollContainerRef} className="sm:flex-1 sm:overflow-y-auto sm:min-h-0 flex flex-col gap-6 pb-4">
        {/* Board columns */}
        {!kanbanHidden && (isLoading ? (
          <div className="text-gray-400 dark:text-gray-500 text-sm">Ładowanie...</div>
        ) : (
          <>
            {/* Mobile: status tabs + single column */}
            <div className="sm:hidden flex flex-col gap-3 flex-shrink-0">
              <div className="flex gap-1.5 overflow-x-auto pb-1">
                {TASK_STATUS_ORDER.map((status) => (
                  <button
                    key={status}
                    onClick={() => setMobileKanbanStatus(status)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 transition-colors ${
                      mobileKanbanStatus === status
                        ? "bg-[var(--color-primary)] text-white"
                        : "bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300"
                    }`}
                  >
                    {TASK_STATUS_LABELS[status]}{" "}
                    <span className="opacity-70">({subtasksByStatus[status].length})</span>
                  </button>
                ))}
              </div>
              <div className="flex flex-col gap-2">
                {subtasksByStatus[mobileKanbanStatus].map((st) => (
                  <SubTaskBoardCard
                    key={st.id}
                    subtask={st}
                    onClick={() => setSelectedSubTask({ taskId: st.task, subtaskId: st.id })}
                  />
                ))}
                {subtasksByStatus[mobileKanbanStatus].length === 0 && (
                  <div className="text-xs text-gray-300 dark:text-gray-600 text-center py-6 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
                    Brak podzadań
                  </div>
                )}
              </div>
            </div>

            {/* Desktop: horizontal scrolling kanban */}
            <div className="hidden sm:flex gap-4 overflow-x-auto h-80 flex-shrink-0">
              {TASK_STATUS_ORDER.map((status) => (
                <div key={status} className="flex flex-col flex-shrink-0 w-72 h-full">
                  <div
                    className={`flex items-center justify-between px-3 py-2 mb-2 rounded-lg border-l-4 bg-gray-50 dark:bg-gray-800/50 ${TASK_STATUS_BORDER[status]}`}
                  >
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      {TASK_STATUS_LABELS[status]}
                    </span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-400 dark:text-gray-500 bg-white dark:bg-gray-800 rounded-full px-2 py-0.5">
                        {subtasksByStatus[status].length}
                      </span>
                      <button
                        onClick={() => setCreateStatus(status)}
                        className="text-gray-400 dark:text-gray-500 hover:text-[var(--color-primary)]"
                        title="Dodaj podzadanie w tej kolumnie"
                      >
                        <Plus size={15} />
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 overflow-y-auto flex-1">
                    {subtasksByStatus[status].map((st) => (
                      <SubTaskBoardCard
                        key={st.id}
                        subtask={st}
                        onClick={() => setSelectedSubTask({ taskId: st.task, subtaskId: st.id })}
                      />
                    ))}
                    {subtasksByStatus[status].length === 0 && (
                      <div className="text-xs text-gray-300 text-center py-6 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
                        Brak podzadań
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        ))}

        {/* Task table section */}
        {!tablePanelHidden && (
        <div className="flex flex-col gap-4 flex-shrink-0 mb-[150px]">
          <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Zadania i podzadania
          </h2>

          {/* Mobile: task accordion cards */}
          <div className="sm:hidden flex flex-col gap-3">
            {filteredTasks.map((task) => {
              const subs = subtasksByTaskId[task.id] ?? [];
              const isExpanded = !collapsedTasks.has(task.id);
              const doneCount = subs.filter((s) => s.status === "done").length;
              const rowColor = stableTaskColor(task.id, TASK_ROW_COLORS);
              return (
                <div key={task.id} className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700">
                  <div
                    className="flex items-center gap-2 px-3 py-3 border-l-4 bg-gray-50 dark:bg-gray-800/50"
                    style={{ borderLeftColor: rowColor }}
                  >
                    <button
                      className="text-gray-400 dark:text-gray-500 flex-shrink-0"
                      onClick={() => toggleExpand(task.id)}
                    >
                      {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                    </button>
                    {subs.length > 0 && (
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleExpandAll(task.id); }}
                        className={`flex-shrink-0 flex items-center transition-colors ${expandAllSet.has(task.id) ? "text-[var(--color-primary)]" : "text-gray-400 dark:text-gray-500 hover:text-[var(--color-primary)]"}`}
                        title={expandAllSet.has(task.id) ? "Zwiń całe drzewo" : "Rozwiń całe drzewo podzadań"}
                      >
                        <span className={`flex items-center transition-transform ${isExpanded ? "rotate-90" : ""}`}>
                          <ChevronRight size={14} />
                          <ChevronRight size={14} className="-ml-2.5" />
                        </span>
                      </button>
                    )}
                    {task.project_name && (
                      <span className="text-xs text-[var(--color-primary)] bg-[var(--bg-elevated)] border border-blue-100 dark:border-blue-800 px-1.5 py-0.5 rounded-full flex-shrink-0">
                        {task.project_name}
                      </span>
                    )}
                    {task.my_category && (
                      <span className="flex items-center gap-1 text-xs px-1.5 py-0.5 rounded-full flex-shrink-0 text-white" style={{ backgroundColor: task.my_category.color }}>
                        {task.my_category.name}
                      </span>
                    )}
                    <button
                      className="flex-1 text-sm font-medium text-gray-800 dark:text-gray-200 truncate text-left"
                      onClick={() => { setSelectedTaskId(task.id); setTaskInitialTab(undefined); }}
                    >
                      {task.title}
                    </button>
                    <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                      {doneCount}/{subs.length}
                    </span>
                  </div>
                  {isExpanded && subs.length > 0 && (
                    <div className="border-t border-gray-100 dark:border-gray-700">
                      <MobileSubtaskGroup
                        subs={subs}
                        parentId={null}
                        depth={0}
                        taskId={task.id}
                        onOpen={(tid, sid) => setSelectedSubTask({ taskId: tid, subtaskId: sid })}
                        forceExpand={expandAllSet.has(task.id)}
                      />
                    </div>
                  )}
                  {isExpanded && subs.length === 0 && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 px-4 py-3 border-t border-gray-100 dark:border-gray-700">
                      Brak podzadań
                    </p>
                  )}
                </div>
              );
            })}
            {filteredTasks.length === 0 && (
              <p className="text-xs text-gray-400 dark:text-gray-500 text-center py-8">Brak zadań.</p>
            )}
          </div>

          {/* Desktop: grid table */}
          <div className="hidden sm:block overflow-x-auto">
          <div className="min-w-[660px]">

          {/* Column headers */}
          <div className="grid grid-cols-[1fr_120px_110px_110px_80px_120px] bg-gray-50 dark:bg-gray-800/50 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-xs font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide">
            <div className="px-4 py-2 border-r-2 border-gray-200 dark:border-gray-600">
              Podzadanie
            </div>
            <div className="px-2 py-2 text-center border-r-2 border-gray-200 dark:border-gray-600">
              Status
            </div>
            <div className="px-2 py-2 text-center border-r-2 border-gray-200 dark:border-gray-600">
              Priorytet
            </div>
            <div className="px-2 py-2 text-center border-r-2 border-gray-200 dark:border-gray-600">
              Termin
            </div>
            <div className="px-2 py-2 text-center border-r-2 border-gray-200 dark:border-gray-600">
              Kom.
            </div>
            <div className="px-3 py-2">Osoby</div>
          </div>

          {filteredTasks.length === 0 && (
            <div className="text-sm text-gray-400 dark:text-gray-500 text-center py-8">
              Brak zadań.
            </div>
          )}
          <div className="flex flex-col gap-4 mt-2">
          {filteredTasks.map((task) => {
            const subs = subtasksByTaskId[task.id] ?? [];
            const rootSubs = subs
              .filter((s) => !s.parent)
              .sort(
                (a, b) =>
                  a.position - b.position ||
                  a.created_at.localeCompare(b.created_at),
              );
            const isExpanded = !collapsedTasks.has(task.id);
            const doneCount = subs.filter((s) => s.status === "done").length;
            const rowColor = stableTaskColor(task.id, TASK_ROW_COLORS);

            return (
              <DroppableTaskZone
                key={task.id}
                taskId={task.id}
                isOver={dragOverTaskId === task.id}
                onDragOver={setDragOverTaskId}
                onDrop={handleDrop}
              >
                <div
                  className={`rounded-xl overflow-hidden bg-white dark:bg-gray-800 group/taskrow transition-[border] ${
                    taskDropIndicator?.id === task.id && taskDropIndicator.pos === "before"
                      ? "border-t-2 border-t-blue-400"
                      : taskDropIndicator?.id === task.id && taskDropIndicator.pos === "after"
                        ? "border-b-2 border-b-blue-400"
                        : ""
                  }`}
                  onDragOver={(e) => {
                    if (e.dataTransfer.types.includes("subtaskid")) return;
                    if (!e.dataTransfer.types.includes("taskid")) return;
                    e.preventDefault();
                    e.stopPropagation();
                    const rect = e.currentTarget.getBoundingClientRect();
                    const pos: "before" | "after" = (e.clientY - rect.top) / rect.height < 0.5 ? "before" : "after";
                    taskDropPosRef.current = pos;
                    if (taskDropIndicator?.id !== task.id || taskDropIndicator?.pos !== pos) {
                      setTaskDropIndicator({ id: task.id, pos });
                    }
                  }}
                  onDragLeave={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget as Node) && taskDropIndicator?.id === task.id) {
                      setTaskDropIndicator(null);
                      taskDropPosRef.current = null;
                    }
                  }}
                  onDrop={(e) => {
                    if (e.dataTransfer.getData("taskId") && !e.dataTransfer.getData("subtaskId")) {
                      e.preventDefault();
                      e.stopPropagation();
                      handleTaskRowDrop(e, task.id, taskDropPosRef.current ?? "before");
                    }
                  }}
                >
                  {/* Task header row */}
                  <div
                    draggable
                    onDragStart={(e) => {
                      e.stopPropagation();
                      e.dataTransfer.effectAllowed = "move";
                      e.dataTransfer.setData("taskId", task.id);
                      dragTaskRef.current = task.id;
                    }}
                    onDragEnd={() => {
                      dragTaskRef.current = null;
                      taskDropPosRef.current = null;
                      setTaskDropIndicator(null);
                    }}
                    className="flex items-center gap-2 px-4 py-3 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-700/50 cursor-pointer border-b border-l-[6px] border-gray-200 dark:border-gray-700"
                    style={{ borderLeftColor: rowColor }}
                    onClick={() => toggleExpand(task.id)}
                  >
                    <GripVertical
                      size={14}
                      className="text-gray-300 dark:text-gray-600 group-hover/taskrow:text-gray-400 dark:group-hover/taskrow:text-gray-500 cursor-grab flex-shrink-0"
                    />
                    <button className="text-gray-400 dark:text-gray-500 flex-shrink-0">
                      {isExpanded ? (
                        <ChevronDown size={14} />
                      ) : (
                        <ChevronRight size={14} />
                      )}
                    </button>
                    {subs.length > 0 && (
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleExpandAll(task.id); }}
                        className={`flex-shrink-0 flex items-center transition-colors ${expandAllSet.has(task.id) ? "text-[var(--color-primary)]" : "text-gray-400 dark:text-gray-500 hover:text-[var(--color-primary)]"}`}
                        title={expandAllSet.has(task.id) ? "Zwiń całe drzewo" : "Rozwiń całe drzewo podzadań"}
                      >
                        <span className={`flex items-center transition-transform ${isExpanded ? "rotate-90" : ""}`}>
                          <ChevronRight size={14} />
                          <ChevronRight size={14} className="-ml-2.5" />
                        </span>
                      </button>
                    )}
                    {task.project_name && (
                      <span className="text-xs text-[var(--color-primary)] bg-[var(--bg-elevated)] border border-blue-100 dark:border-blue-800 px-2 py-0.5 rounded-full flex-shrink-0">
                        {task.project_name}
                      </span>
                    )}
                    {task.my_category && (
                      <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full flex-shrink-0 text-white" style={{ backgroundColor: task.my_category.color }}>
                        {task.my_category.name}
                      </span>
                    )}
                    <TaskTitleCell
                      task={task}
                      color={rowColor}
                      onSingleClick={() => { setSelectedTaskId(task.id); setTaskInitialTab(undefined); }}
                    />
                    {task.is_archived && (
                      <span className="flex items-center gap-1 text-xs px-2 py-0.5 rounded-full flex-shrink-0 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800">
                        <Archive size={11} />
                        Zarchiwizowane
                      </span>
                    )}
                    {task.comments_count > 0 && (
                      <div
                        className="flex items-center gap-1 text-xs text-gray-400 dark:text-gray-500 flex-shrink-0"
                        title="Komentarze do zadania"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MessageSquare size={12} />
                        <span>{task.comments_count}</span>
                        {task.last_comment_at && (
                          <span className="hidden xl:inline">· {formatTimeAgo(task.last_comment_at)}</span>
                        )}
                      </div>
                    )}
                    {subs.length > 0 && (
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <div className="w-48 h-3.5 bg-gray-200 dark:bg-gray-700 rounded-sm overflow-hidden flex">
                          {(
                            [
                              "done",
                              "todo_now",
                              "in_progress",
                              "waiting",
                              "todo_later",
                              "cancelled",
                              "n_d",
                            ] as TaskStatus[]
                          ).map((st) => {
                            const count = subs.filter(
                              (s) => s.status === st,
                            ).length;
                            if (count === 0) return null;
                            const pct = (count / subs.length) * 100;
                            const color =
                              st === "done"
                                ? "bg-emerald-500"
                                : st === "todo_now"
                                  ? "bg-blue-500"
                                  : st === "in_progress"
                                    ? "bg-violet-400"
                                    : st === "waiting"
                                      ? "bg-yellow-400"
                                      : st === "todo_later"
                                        ? "bg-gray-400"
                                        : st === "n_d"
                                          ? "bg-slate-300"
                                          : "bg-rose-400";
                            return (
                              <div
                                key={st}
                                className={`h-full ${color} transition-all`}
                                style={{ width: `${pct}%` }}
                                title={`${TASK_STATUS_LABELS[st]}: ${count}`}
                              />
                            );
                          })}
                        </div>
                        <span className="text-xs text-gray-400 dark:text-gray-500 w-10 text-right">
                          {doneCount}/{subs.length}
                        </span>
                      </div>
                    )}
                    {subs.length === 0 && (
                      <span className="text-xs text-gray-300 dark:text-gray-600 flex-shrink-0">
                        Brak podzadań
                      </span>
                    )}
                    <TaskPeopleCell task={task} users={users} />
                    {task.can_archive && (
                      task.is_archived ? (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            archiveTask({ id: task.id, archived: false });
                          }}
                          title="Przywróć zadanie z archiwum"
                          className="flex-shrink-0 opacity-0 group-hover/taskrow:opacity-100 transition-opacity p-1 rounded hover:bg-emerald-100 dark:hover:bg-emerald-900/40 text-gray-400 hover:text-emerald-600"
                        >
                          <ArchiveRestore size={14} />
                        </button>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setTaskToArchive(task);
                          }}
                          title="Archiwizuj zadanie"
                          className="flex-shrink-0 opacity-0 group-hover/taskrow:opacity-100 transition-opacity p-1 rounded hover:bg-amber-100 dark:hover:bg-amber-900/40 text-gray-400 hover:text-amber-600"
                        >
                          <Archive size={14} />
                        </button>
                      )
                    )}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        pinTask(task.id);
                      }}
                      title={task.is_pinned ? "Odepnij zadanie" : "Przypnij zadanie na górze"}
                      className={`flex-shrink-0 transition-opacity p-1 rounded hover:bg-yellow-100 dark:hover:bg-yellow-900/40 ${task.is_pinned ? "text-yellow-500" : "opacity-0 group-hover/taskrow:opacity-100 text-gray-400 hover:text-yellow-500"}`}
                    >
                      <Pin size={14} className={task.is_pinned ? "fill-yellow-500" : ""} />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (
                          !window.confirm(
                            `Usunąć zadanie "${task.title}" wraz ze wszystkimi podzadaniami?`,
                          )
                        )
                          return;
                        deleteTask(task.id);
                      }}
                      title="Usuń zadanie"
                      className="flex-shrink-0 opacity-0 group-hover/taskrow:opacity-100 transition-opacity p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/40 text-gray-400 hover:text-red-600"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>

                  {/* Subtask tree */}
                  {isExpanded && (
                    <div>
                      {rootSubs.length === 0 && (
                        <div className="px-8 py-3 text-xs text-gray-400 dark:text-gray-500 italic">
                          Brak podzadań.
                        </div>
                      )}
                      {rootSubs.map((st) => (
                        <SubTaskTableTree
                          key={st.id}
                          subtask={st}
                          allForTask={subs}
                          depth={0}
                          taskId={task.id}
                          onOpen={(id) =>
                            setSelectedSubTask({
                              taskId: task.id,
                              subtaskId: id,
                            })
                          }
                          users={users}
                          addingChildToId={addingChildToId}
                          onStartAdd={setAddingChildToId}
                          onDoneAdd={() => setAddingChildToId(null)}
                          draggingSubtask={draggingSubtask}
                          onDragStart={setDraggingSubtask}
                          onReorder={handleReorder}
                          onReparent={handleReparent}
                          forceExpand={expandAllSet.has(task.id)}
                          selectedSubtaskIds={selectedSubtaskIds}
                          onToggleSelect={handleToggleSelect}
                        />
                      ))}

                      {/* Root-level quick-add */}
                      {addingToTask === task.id ? (
                        <InlineSubTaskAdd
                          taskId={task.id}
                          onDone={() => setAddingToTask(null)}
                        />
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setAddingToTask(task.id);
                          }}
                          className="w-full text-left pl-5 px-4 py-2 text-xs text-gray-400 dark:text-gray-500 hover:text-[var(--color-primary)] hover:bg-[var(--color-primary-dim)]/50 transition-colors flex items-center gap-1.5"
                        >
                          <Plus size={11} />
                          Dodaj podzadanie
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </DroppableTaskZone>
            );
          })}
          </div>
          </div>
          </div>
        </div>
        )}
      </div>

      {selectedTaskId && (
        <TaskDetailPanel
          taskId={selectedTaskId}
          onClose={() => { setSelectedTaskId(null); setTaskInitialTab(undefined); setTargetCommentId(null); }}
          initialTab={taskInitialTab}
          targetCommentId={targetCommentId}
        />
      )}

      {selectedSubTask && !selectedTaskId && (
        <SubTaskDetailPanel
          taskId={selectedSubTask.taskId}
          subtaskId={selectedSubTask.subtaskId}
          onClose={() => setSelectedSubTask(null)}
        />
      )}

      {createStatus !== null && (
        <CreateSubTaskModal
          open
          onClose={() => setCreateStatus(null)}
          defaultStatus={createStatus}
          tasks={tasksForModal}
          users={users}
        />
      )}

      <CreateTaskModal
        open={createTaskOpen}
        onClose={() => setCreateTaskOpen(false)}
        projects={projects}
      />

      {cloneOpen && (
        <CloneTasksModal
          tasks={allTasks}
          allTaskIds={allTasks.map((t) => t.id)}
          onClose={() => setCloneOpen(false)}
        />
      )}

      {taskToArchive && createPortal(
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4" onClick={() => setTaskToArchive(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-md flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-3 px-6 pt-6">
              <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center flex-shrink-0">
                <Archive size={20} className="text-amber-600 dark:text-amber-400" />
              </div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                Archiwizuj zadanie
              </h2>
            </div>
            <div className="px-6 py-4">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Czy na pewno chcesz zarchiwizować zadanie{" "}
                <span className="font-medium text-gray-900 dark:text-gray-100">„{taskToArchive.title}”</span>?
                Zniknie ono z głównego widoku, ale pozostanie dostępne po zaznaczeniu opcji „Pokaż zarchiwizowane”.
              </p>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-100 dark:border-gray-700">
              <button
                onClick={() => setTaskToArchive(null)}
                disabled={isArchiving}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 disabled:opacity-50"
              >
                Anuluj
              </button>
              <button
                onClick={() => {
                  const id = taskToArchive.id;
                  archiveTask(
                    { id, archived: true },
                    { onSuccess: () => setTaskToArchive(null) },
                  );
                }}
                disabled={isArchiving}
                className="px-4 py-2 text-sm rounded-lg bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50"
              >
                {isArchiving ? "Archiwizowanie…" : "Archiwizuj"}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
}
